import { ENEMIES } from '../../data/enemies';
import { GROUND_Y, SIM_HZ, WORLD_TOP } from '../../data/tuning';
import { createAabb, type Aabb } from '../collision';

/**
 * EN-33 Tren del tequila (GDD §8.3, issue #118). A ground-level train with a chimney whose smoke rises in
 * columns and never harms: it blocks the view, so the hazard is about memory and reading silhouettes, never
 * a hidden hitbox (GDD §2, pillar 2). The body is an AABB resting on the ground; the smoke columns are pure
 * render data (position and age) and do not take part in collisions or triggers (#34). Pure and
 * deterministic: the train moves with the world from its start x and the ticks since it was active.
 */

export const EN33 = ENEMIES['EN-33'];

export const SMOKE_TICKS = Math.round(EN33.smokeEverySeconds * SIM_HZ);
export const SMOKE_LIFE_TICKS = Math.round(EN33.smokeSeconds * SIM_HZ);

export interface SmokeColumn {
  x: number;
  /** Ticks since it was emitted; it rises from the chimney with age. */
  age: number;
}

export interface TrainState {
  /** Ticks since it was first active. */
  ticks: number;
  /** Ticks since the last smoke column. */
  sinceSmoke: number;
  smoke: SmokeColumn[];
  /** Whether the whistle has been sounded (once, on entering the screen). */
  whistled: boolean;
}

export const createTrain = (): TrainState => ({ ticks: 0, sinceSmoke: SMOKE_TICKS, smoke: [], whistled: false });

/** x of the train `ticks` ticks after it was first active: it moves with the player, slower than the scroll. */
export const trainX = (startX: number, ticks: number): number => startX + (EN33.worldSpeed * ticks) / SIM_HZ;

/** The body of the train: an AABB resting on the ground (its bottom is at y 1, GDD §5.2). */
export const trainBody = (x: number): Aabb =>
  createAabb(x, GROUND_Y + EN33.hitboxHeight / 2, EN33.hitboxWidth, EN33.hitboxHeight);

/** The chimney sits at the front of the train; smoke columns start there. */
export const chimneyX = (x: number): number => x - EN33.hitboxWidth / 2 + 1;

/**
 * One tick: the smoke ages and the train emits a column from the chimney every `SMOKE_TICKS` while it is on
 * screen; expired columns (older than `SMOKE_LIFE_TICKS`) go. Returns the x of a new column when one was
 * emitted, else null. The train's own position is set by the caller from `trainX`.
 */
export function stepTrain(state: TrainState, x: number, onScreen: boolean): number | null {
  state.ticks += 1;
  for (const column of state.smoke) column.age += 1;
  state.smoke = state.smoke.filter((column) => column.age < SMOKE_LIFE_TICKS);
  state.sinceSmoke += 1;
  if (!onScreen || state.sinceSmoke < SMOKE_TICKS) return null;
  state.sinceSmoke = 0;
  state.smoke.push({ x, age: 0 });
  return x;
}

/** Height of the top of a smoke column by its age: it rises from the chimney to the ceiling, then holds. */
export const smokeTopY = (age: number): number => {
  const base = GROUND_Y + EN33.hitboxHeight;
  return Math.min(WORLD_TOP, base + (EN33.smokeRiseSpeed * age) / SIM_HZ);
};

/** Bottom of a smoke column, on the chimney. */
export const smokeBottomY = (): number => GROUND_Y + EN33.hitboxHeight - 0.4;
